import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Chip, 
    Paper,
    IconButton,
    Stack,
    Grid,
    useTheme,
    TextField,
    CircularProgress,
    Alert,
    Select,
    MenuItem,
    FormControl,
    ToggleButton,
    ToggleButtonGroup,
    Badge,
    Card,
    CardContent,
    Collapse,
    Fade,
    Grow,
    Zoom,
    Avatar,
    Divider,
    useMediaQuery,
    InputLabel,
    Tooltip,
    Skeleton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Switch,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import { 
    ChevronLeft, 
    ChevronRight, 
    Schedule,
    CalendarMonth,
    AccessTime,
    LocationOn,
    Event,
    Refresh,
    CheckCircle,
    EventBusy,
    ArrowBack,
    AutoAwesome,
    CalendarToday,
    Update,
    Tune,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Save,
    Close,
    Edit,
    Public as WorldIcon,
    Person,
    Notes,
    Title,
    Delete,
    ExpandMore,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAppDispatch } from 'src/store';
import { executeConversationFunction } from 'src/features/account/conversations.slice';
import { enqueueSnackbar } from 'notistack';
import { addDays, startOfDay, endOfDay, isSameDay, format, isToday, isTomorrow, isYesterday } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

interface CalendarData {
    available_slots: Array<{
        start_time: string;
        end_time: string;
        duration?: number;
        [key: string]: any;
    }>;
    calendar_timezone: string;
    contact_timezone: string;
    start_date: string;
    end_date: string;
    meeting_duration: number;
}

interface CalendarViewProps {
    calendarData: CalendarData;
    conversationId: string;
    channelId: string;
    messageId?: string;
}

interface BookingFormData {
    client_notes: string;
    meeting_duration: number;
    location: string;
    summary: string;
    allow_double_booking: boolean;
    attendees: string[]; // Add attendees field
    send_notifications: boolean; // Add send notifications field
    description: string; // Add description field
}

interface CreatedEvent {
    id: string;
    summary: string;
    start: { date_time: string; time_zone: string };
    end: { date_time: string; time_zone: string };
    location?: string;
    description?: string;
    html_link?: string;
    attendees?: Array<{ 
        email: string; 
        display_name?: string;
        organizer?: boolean;
        response_status?: string;
        self?: boolean;
    }>;
}

type ViewState = 'calendar' | 'booking' | 'confirmed' | 'event_details';

// Animated components
const MotionPaper = motion(Paper);
const MotionCard = motion(Card);
const MotionBox = motion(Box);

// Enhanced date formatting helpers
const formatDateLabel = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'EEE, MMM d');
};

const CalendarView: React.FC<CalendarViewProps> = ({ 
    calendarData, 
    conversationId, 
    channelId,
    messageId 
}) => {
    // Debug logging for CalendarView rendering
    console.log('📅 CalendarView component rendered with props:', {
        calendarData,
        conversationId,
        channelId,
        messageId,
        hasAvailableSlots: calendarData?.available_slots,
        slotsCount: Array.isArray(calendarData?.available_slots) ? calendarData.available_slots.length : 'not array'
    });

    const theme = useTheme();
    const dispatch = useAppDispatch();
    const darkMode = theme.palette.mode === 'dark';
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    // View state management
    const [viewState, setViewState] = useState<ViewState>('calendar');
    const [selectedSlot, setSelectedSlot] = useState<typeof calendarData.available_slots[0] | null>(null);
    const [createdEvent, setCreatedEvent] = useState<CreatedEvent | null>(null);
    
    // Calendar state
    const [selectedDate, setSelectedDate] = useState<string | null>(null);
    const [viewMode, setViewMode] = useState<'day' | 'week'>('day');
    const [isLoadingSlots, setIsLoadingSlots] = useState(false);
    
    // Add range selection state
    const [dateRangeStart, setDateRangeStart] = useState<string | null>(null);
    const [dateRangeEnd, setDateRangeEnd] = useState<string | null>(null);
    const [isSelectingRange, setIsSelectingRange] = useState(false);
    
    // Booking state
    const [isBooking, setIsBooking] = useState(false);
    const [bookingError, setBookingError] = useState<string | null>(null);
    const [forceBooking, setForceBooking] = useState(false);
    
    // Enhanced booking form state
    const [bookingForm, setBookingForm] = useState<BookingFormData>({
        client_notes: '',
        meeting_duration: calendarData.meeting_duration,
        location: '',
        summary: '',
        allow_double_booking: false,
        attendees: [], // Initialize attendees
        send_notifications: true, // Default to sending notifications
        description: '' // Initialize description
    });
    
    // Floating parameter chips state
    const [editingParam, setEditingParam] = useState<string | null>(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    
    // Clean parameter state without auto-collapse
    const [duration, setDuration] = useState(calendarData.meeting_duration);
    const [address, setAddress] = useState('');
    const [timezone, setTimezone] = useState(calendarData.contact_timezone || calendarData.calendar_timezone);
    
    // Auto-collapse timers
    useEffect(() => {
        if (editingParam) {
            const timer = setTimeout(() => setEditingParam(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [editingParam]);

    // Parse and group slots by date
    const slotsByDate = useMemo(() => {
        if (!Array.isArray(calendarData.available_slots)) {
            return new Map<string, typeof calendarData.available_slots>();
        }

        const grouped = new Map<string, typeof calendarData.available_slots>();
        
        calendarData.available_slots.forEach(slot => {
            const date = new Date(slot.start_time);
            const dateKey = date.toISOString().split('T')[0];
            
            if (!grouped.has(dateKey)) {
                grouped.set(dateKey, []);
            }
            grouped.get(dateKey)?.push(slot);
        });
        
        const sortedDates = Array.from(grouped.keys()).sort();
        const sortedMap = new Map<string, typeof calendarData.available_slots>();
        
        sortedDates.forEach(date => {
            const slots = grouped.get(date) || [];
            slots.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
            sortedMap.set(date, slots);
        });
        
        return sortedMap;
    }, [calendarData.available_slots]);

    // Get first date with availability
    const firstAvailableDate = useMemo(() => {
        const dates = Array.from(slotsByDate.keys()).sort();
        return dates.length > 0 ? dates[0] : null;
    }, [slotsByDate]);

    // Enhanced date generation - show more dates including unfetched ones
    const [dateOffset, setDateOffset] = useState(0); // Track weeks from today
    
    const allDates = useMemo(() => {
        const startDate = new Date();
        startDate.setDate(startDate.getDate() + (dateOffset * 7)); // Add weeks
        const dateArray: string[] = [];
        
        // Generate 3 weeks of dates starting from the offset
        for (let i = 0; i < 21; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);
            dateArray.push(date.toISOString().split('T')[0]);
        }
        
        return dateArray;
    }, [dateOffset]);
    
    // Initialize selected date to first available date
    useEffect(() => {
        if (!selectedDate && firstAvailableDate) {
            setSelectedDate(firstAvailableDate);
        }
    }, [firstAvailableDate, selectedDate]);
    
    const activeDate = selectedDate || firstAvailableDate || allDates[0];
    const activeSlots = activeDate ? slotsByDate.get(activeDate) || [] : [];
    
    // Check if a date has been fetched (has data in slotsByDate)
    const isDateFetched = useCallback((date: string) => {
        return slotsByDate.has(date);
    }, [slotsByDate]);
    
    // Handle unfetched date clicks
    const handleDateClick = useCallback(async (date: string) => {
        setSelectedDate(date);
        
        if (!isDateFetched(date)) {
            setIsLoadingSlots(true);
            try {
                // Create explicit date with full day coverage
                const targetDate = new Date(date + 'T00:00:00.000Z');
                
                // Use startOfDay and endOfDay to ensure we get the full day range
                // This ensures no appointments are missed during the day
                const dayStart = startOfDay(targetDate);
                const dayEnd = endOfDay(targetDate);
                
                console.log('📅 Fetching single date:', {
                    dayStart: dayStart.toISOString(),
                    dayEnd: dayEnd.toISOString(),
                    originalDate: date
                });
                
                await dispatch(executeConversationFunction({
                    conversationId,
                    functionName: 'get_available_timeslots',
                    functionArguments: {
                        start_date: dayStart.toISOString(),
                        end_date: dayEnd.toISOString(),
                        meeting_duration: bookingForm.meeting_duration,
                        ...(bookingForm.location ? { address: bookingForm.location } : {})
                    },
                    messageId
                })).unwrap();
            } catch (error: any) {
                enqueueSnackbar(error.message || 'Error fetching time slots', { variant: 'error' });
            } finally {
                setIsLoadingSlots(false);
            }
        }
    }, [dispatch, conversationId, isDateFetched, bookingForm.meeting_duration, bookingForm.location, messageId]);
    
    // Handle 7-day navigation
    const handleDateNavigation = useCallback((direction: 'previous' | 'next') => {
        const newOffset = direction === 'next' ? dateOffset + 1 : Math.max(0, dateOffset - 1);
        setDateOffset(newOffset);
        setSelectedDate(null); // Reset selection when navigating
    }, [dateOffset]);

    // Group slots by hour
    const slotsByHour = useMemo(() => {
        const grouped = new Map<number, typeof activeSlots>();
        
        activeSlots.forEach(slot => {
            const hour = new Date(slot.start_time).getHours();
            if (!grouped.has(hour)) {
                grouped.set(hour, []);
            }
            grouped.get(hour)?.push(slot);
        });
        
        return Array.from(grouped.entries()).sort(([a], [b]) => a - b);
    }, [activeSlots]);

    // Format helpers
    const formatDate = useCallback((dateStr: string, showYear = false) => {
        try {
            const date = new Date(dateStr + 'T00:00:00');
            const options: Intl.DateTimeFormatOptions = { 
                weekday: 'short', 
                month: 'short', 
                day: 'numeric'
            };
            if (showYear) options.year = 'numeric';
            return date.toLocaleDateString('en-US', options);
        } catch (error) {
            return dateStr;
        }
    }, []);
    
    const formatTime = useCallback((timeStr: string, showTimezone = false) => {
        try {
            const date = new Date(timeStr);
            const timeString = date.toLocaleTimeString('en-US', { 
                hour: 'numeric', 
                minute: '2-digit',
                hour12: true 
            });
            
            if (showTimezone && calendarData.contact_timezone) {
                return `${timeString} ${calendarData.contact_timezone}`;
            }
            
            return timeString;
        } catch (error) {
            return 'Invalid time';
        }
    }, [calendarData.contact_timezone]);

    // Handle slot selection
    const handleSlotSelect = useCallback((slot: typeof calendarData.available_slots[0]) => {
        setSelectedSlot(slot);
        // Pre-populate form with smart defaults
        setBookingForm(prev => ({
            ...prev,
            summary: prev.summary || `Meeting on ${formatDate(slot.start_time.split('T')[0], true)} at ${formatTime(slot.start_time)}`
        }));
        setViewState('booking');
        setBookingError(null);
    }, []);

    // Handle booking form updates
    const updateBookingForm = useCallback((field: keyof BookingFormData, value: any) => {
        setBookingForm(prev => ({ ...prev, [field]: value }));
        if (field === 'meeting_duration' || field === 'location') {
            // Clear selected date to trigger refetch with new params
            setSelectedDate(null);
        }
    }, []);

    // Add state for update dialog
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [updateForm, setUpdateForm] = useState<{
        summary: string;
        location: string;
        description: string;
        attendees: string[];
    }>({
        summary: '',
        location: '',
        description: '',
        attendees: []
    });

    // Add event management handlers
    const handleUpdateEvent = useCallback(async () => {
        if (!createdEvent) return;
        
        // Initialize update form with current event data
        setUpdateForm({
            summary: createdEvent.summary || '',
            location: createdEvent.location || '',
            description: createdEvent.description || '',
            attendees: createdEvent.attendees?.map(a => a.email).filter(email => email) || []
        });
        setUpdateDialogOpen(true);
    }, [createdEvent]);

    const handleUpdateSubmit = useCallback(async () => {
        if (!createdEvent?.id) return;
        
        try {
            const updates: any = {};
            
            // Only include changed fields
            if (updateForm.summary !== createdEvent.summary) updates.summary = updateForm.summary;
            if (updateForm.location !== createdEvent.location) updates.location = updateForm.location;
            if (updateForm.description !== createdEvent.description) updates.description = updateForm.description;
            
            // Always include attendees if changed
            const currentAttendeeEmails = createdEvent.attendees?.map(a => a.email).filter(email => email) || [];
            const attendeesChanged = JSON.stringify(updateForm.attendees.sort()) !== JSON.stringify(currentAttendeeEmails.sort());
            
            if (attendeesChanged) {
                updates.attendees = updateForm.attendees.map(email => ({ email }));
            }
            
            // Only proceed if there are updates
            if (Object.keys(updates).length === 0) {
                enqueueSnackbar('No changes to update', { variant: 'info' });
                setUpdateDialogOpen(false);
                return;
            }
            
            const result = await dispatch(executeConversationFunction({
                conversationId,
                functionName: 'update_event',
                functionArguments: {
                    id: createdEvent.id,
                    updates
                },
                messageId
            })).unwrap();
            
            if (result.success) {
                enqueueSnackbar('Event updated successfully', { variant: 'success' });
                setUpdateDialogOpen(false);
                
                // Update the created event with new data
                setCreatedEvent(prev => prev ? {
                    ...prev,
                    ...updates,
                    attendees: updates.attendees || prev.attendees
                } : null);
            } else {
                enqueueSnackbar('Failed to update event', { variant: 'error' });
            }
        } catch (error: any) {
            enqueueSnackbar(error.message || 'Error updating event', { variant: 'error' });
        }
    }, [dispatch, conversationId, createdEvent, updateForm, messageId]);

    const handleRescheduleEvent = useCallback(async () => {
        if (!createdEvent) return;
        
        // Return to calendar view to select new time
        setViewState('calendar');
        setSelectedSlot(null);
        setCreatedEvent(null);
        enqueueSnackbar('Please select a new time for your appointment', { variant: 'info' });
    }, []);

    const handleCancelEvent = useCallback(async () => {
        if (!createdEvent?.id) return;
        
        try {
            const result = await dispatch(executeConversationFunction({
                conversationId,
                functionName: 'destroy_event',
                functionArguments: {
                    id: createdEvent.id
                },
                messageId
            })).unwrap();
            
            if (result.success) {
                enqueueSnackbar('Appointment cancelled successfully', { variant: 'success' });
                setViewState('calendar');
                setSelectedSlot(null);
                setCreatedEvent(null);
            } else {
                enqueueSnackbar('Failed to cancel appointment', { variant: 'error' });
            }
        } catch (error: any) {
            enqueueSnackbar(error.message || 'Error cancelling appointment', { variant: 'error' });
        }
    }, [dispatch, conversationId, createdEvent, messageId]);

    // Enhanced booking handler
    const handleBooking = useCallback(async () => {
        if (!selectedSlot) return;
        
        setIsBooking(true);
        setBookingError(null);
        
        try {
            const result = await dispatch(executeConversationFunction({
                conversationId,
                functionName: 'create_and_confirm_provider_booking',
                functionArguments: {
                    chosen_time: selectedSlot.start_time,
                    meeting_duration: bookingForm.meeting_duration,
                    location: bookingForm.location || undefined,
                    summary: bookingForm.summary || `Meeting on ${formatDate(selectedSlot.start_time.split('T')[0], true)}`,
                    client_notes: bookingForm.client_notes || undefined,
                    allow_double_booking: bookingForm.allow_double_booking,
                    attendees: bookingForm.attendees.length > 0 ? bookingForm.attendees : undefined,
                    send_notifications: bookingForm.send_notifications,
                    description: bookingForm.description || undefined
                },
                messageId
            })).unwrap();
            
            // Fix: Check the correct path for booking_details
            if (result.success && result.response?.metadata?.booking_details) {
                const bookingDetails = result.response.metadata.booking_details;
                setCreatedEvent({
                    id: bookingDetails.appointment_id,
                    summary: bookingDetails.summary,
                    start: { 
                        date_time: bookingDetails.datetime,
                        time_zone: bookingDetails.timezone 
                    },
                    end: { 
                        date_time: bookingDetails.datetime, // Will calculate end time
                        time_zone: bookingDetails.timezone 
                    },
                    location: bookingDetails.location,
                    html_link: bookingDetails.link,
                    attendees: bookingDetails.attendees
                });
                setViewState('confirmed');
                enqueueSnackbar('Appointment booked successfully!', { variant: 'success' });
            } else {
                const errorResponse = result.response || result;
                const errorMessage = errorResponse?.metadata?.error_message || result.error || 'Failed to book appointment';
                
                // Check for double booking errors in various ways
                const isDoubleBooking = errorResponse?.metadata?.error_type === 'double_booking' ||
                                       errorMessage.toLowerCase().includes('conflict') ||
                                       errorMessage.toLowerCase().includes('double booking') ||
                                       errorMessage.toLowerCase().includes('already booked');
                
                if (isDoubleBooking) {
                    setBookingError(errorMessage);
                    updateBookingForm('allow_double_booking', true);
                    // Auto-expand advanced settings to show the double booking option
                    setShowAdvanced(true);
                } else {
                    setBookingError(errorMessage);
                    enqueueSnackbar(errorMessage, { variant: 'error' });
                }
            }
        } catch (error: any) {
            const errorResponse = error.response || error;
            const errorMessage = errorResponse?.metadata?.error_message || error.message || 'Failed to book appointment';
            
            // Check for double booking errors in various ways
            const isDoubleBooking = errorResponse?.metadata?.error_type === 'double_booking' ||
                                   errorMessage.toLowerCase().includes('conflict') ||
                                   errorMessage.toLowerCase().includes('double booking') ||
                                   errorMessage.toLowerCase().includes('already booked');
            
            if (isDoubleBooking) {
                setBookingError(errorMessage);
                updateBookingForm('allow_double_booking', true);
                // Auto-expand advanced settings to show the double booking option
                setShowAdvanced(true);
            } else {
                setBookingError(errorMessage);
                enqueueSnackbar(errorMessage, { variant: 'error' });
            }
        } finally {
            setIsBooking(false);
        }
    }, [dispatch, conversationId, selectedSlot, bookingForm, formatTime, formatDate, messageId, updateBookingForm]);

    // Handle date range changes
    const handleFetchMoreDates = useCallback(async (direction: 'next' | 'previous') => {
        setIsLoadingSlots(true);
        
        try {
            const baseDate = direction === 'next' 
                ? new Date(calendarData.end_date)
                : new Date(calendarData.start_date);
            
            let newStartDate, newEndDate;
            if (direction === 'next') {
                newStartDate = addDays(baseDate, 1);
                newEndDate = addDays(newStartDate, 7);
            } else {
                newEndDate = addDays(baseDate, -1);
                newStartDate = addDays(newEndDate, -7);
            }
            
            // Ensure full day coverage for the new date range
            const rangeStart = startOfDay(newStartDate);
            const rangeEnd = endOfDay(newEndDate);
            
            console.log('📅 Fetching more dates:', {
                direction,
                rangeStart: rangeStart.toISOString(),
                rangeEnd: rangeEnd.toISOString(),
                originalRange: `${newStartDate.toISOString()} to ${newEndDate.toISOString()}`
            });
            
            const result = await dispatch(executeConversationFunction({
                conversationId,
                functionName: 'get_available_timeslots',
                functionArguments: {
                    start_date: rangeStart.toISOString(),
                    end_date: rangeEnd.toISOString(),
                    meeting_duration: duration,
                    ...(address ? { address } : {})
                },
                messageId
            })).unwrap();
            
            if (result.success) {
                enqueueSnackbar('Calendar updated successfully', { variant: 'success' });
            }
        } catch (error: any) {
            enqueueSnackbar(error.message || 'Error fetching calendar slots', { variant: 'error' });
        } finally {
            setIsLoadingSlots(false);
        }
    }, [dispatch, conversationId, calendarData.end_date, calendarData.start_date, duration, address, messageId]);

    // Enhanced time period grouping
    const timePeriodsSlots = useMemo(() => {
        const periods = {
            morning: { label: 'Morning', slots: [], color: theme.palette.primary.main, icon: '🌅' },
            afternoon: { label: 'Afternoon', slots: [], color: theme.palette.warning.main, icon: '☀️' },
            evening: { label: 'Evening', slots: [], color: theme.palette.secondary.main, icon: '🌆' }
        };
        
        activeSlots.forEach(slot => {
            const hour = new Date(slot.start_time).getHours();
            if (hour < 12) {
                periods.morning.slots.push(slot);
            } else if (hour < 17) {
                periods.afternoon.slots.push(slot);
            } else {
                periods.evening.slots.push(slot);
            }
        });
        
        return Object.entries(periods).filter(([_, period]) => period.slots.length > 0);
    }, [activeSlots, theme]);

    // Get all slots grouped by date and time period
    const allSlotsGrouped = useMemo(() => {
        const result: Array<{
            date: string;
            dateLabel: string;
            periods: {
                morning: typeof calendarData.available_slots;
                afternoon: typeof calendarData.available_slots;
                evening: typeof calendarData.available_slots;
            };
            totalSlots: number;
        }> = [];

        slotsByDate.forEach((slots, date) => {
            const periods = {
                morning: [] as typeof slots,
                afternoon: [] as typeof slots,
                evening: [] as typeof slots
            };

            slots.forEach(slot => {
                const hour = new Date(slot.start_time).getHours();
                if (hour < 12) {
                    periods.morning.push(slot);
                } else if (hour < 17) {
                    periods.afternoon.push(slot);
                } else {
                    periods.evening.push(slot);
                }
            });

            result.push({
                date,
                dateLabel: formatDateLabel(date),
                periods,
                totalSlots: slots.length
            });
        });

        return result.sort((a, b) => a.date.localeCompare(b.date));
    }, [slotsByDate]);

    // Handle date range selection
    const handleDateRangeClick = useCallback((date: string) => {
        if (!isSelectingRange) {
            // Start range selection
            setDateRangeStart(date);
            setDateRangeEnd(null);
            setSelectedDate(date);
        } else {
            // Complete range selection
            if (dateRangeStart) {
                const start = new Date(dateRangeStart);
                const end = new Date(date);
                
                // Ensure start is before end
                if (start <= end) {
                    setDateRangeEnd(date);
                } else {
                    setDateRangeEnd(dateRangeStart);
                    setDateRangeStart(date);
                }
            }
        }
    }, [isSelectingRange, dateRangeStart]);
    
    // Fetch slots for date range
    const fetchDateRange = useCallback(async () => {
        if (!dateRangeStart || !dateRangeEnd) return;
        
        setIsLoadingSlots(true);
        try {
            // Create explicit start and end dates to ensure full day coverage
            // Start at 00:00:00.000 of the first day
            const startDate = new Date(dateRangeStart + 'T00:00:00.000Z');
            // End at 23:59:59.999 of the last day
            const endDate = new Date(dateRangeEnd + 'T23:59:59.999Z');
            
            // Use startOfDay and endOfDay to ensure we get the full day range
            // This ensures no appointments are missed at the beginning or end of the range
            const rangeStart = startOfDay(startDate);
            const rangeEnd = endOfDay(endDate);
            
            console.log('📅 Fetching date range:', {
                rangeStart: rangeStart.toISOString(),
                rangeEnd: rangeEnd.toISOString(),
                originalRange: `${dateRangeStart} to ${dateRangeEnd}`
            });
            
            await dispatch(executeConversationFunction({
                conversationId,
                functionName: 'get_available_timeslots',
                functionArguments: {
                    start_date: rangeStart.toISOString(),
                    end_date: rangeEnd.toISOString(),
                    meeting_duration: bookingForm.meeting_duration,
                    ...(bookingForm.location ? { address: bookingForm.location } : {})
                },
                messageId
            })).unwrap();
            
            // Clear range selection after fetching
            setIsSelectingRange(false);
            setDateRangeStart(null);
            setDateRangeEnd(null);
        } catch (error: any) {
            enqueueSnackbar(error.message || 'Error fetching time slots', { variant: 'error' });
        } finally {
            setIsLoadingSlots(false);
        }
    }, [dispatch, conversationId, dateRangeStart, dateRangeEnd, bookingForm.meeting_duration, bookingForm.location, messageId]);

    // Render different views based on state
    const renderCalendarView = () => (
        <>
            {/* Beautiful Header Section - More Compact */}
            <Box
                sx={{ mb: { xs: 2, sm: 3 } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: { xs: 1.5, sm: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                        <Avatar
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                color: theme.palette.primary.main,
                                width: { xs: 36, sm: 40 },
                                height: { xs: 36, sm: 40 },
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                        >
                            <CalendarMonth sx={{ fontSize: { xs: 20, sm: 24 } }} />
                        </Avatar>
                        <Box>
                            <Typography 
                                variant="h5" 
                                sx={{ 
                                    fontWeight: 700,
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    mb: 0,
                                    fontSize: { xs: '1.2rem', sm: '1.5rem' },
                                }}
                            >
                                Available Appointments
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                Select a date or range to view time slots
                            </Typography>
                        </Box>
                    </Box>
                    
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Range selection toggle */}
                        <ToggleButton
                            value="range"
                            selected={isSelectingRange}
                            onChange={() => {
                                setIsSelectingRange(!isSelectingRange);
                                setDateRangeStart(null);
                                setDateRangeEnd(null);
                            }}
                            size="small"
                            sx={{
                                px: { xs: 1, sm: 2 },
                                py: { xs: 0.5, sm: 1 },
                                borderRadius: 2,
                                fontSize: { xs: '0.7rem', sm: '0.8rem' },
                            }}
                        >
                            <CalendarToday sx={{ fontSize: { xs: 14, sm: 16 }, mr: 0.5 }} />
                            Range
                        </ToggleButton>
                        
                        {/* Fetch range button */}
                        {dateRangeStart && dateRangeEnd && (
                            <Button
                                variant="contained"
                                size="small"
                                onClick={fetchDateRange}
                                disabled={isLoadingSlots}
                                sx={{
                                    fontSize: { xs: '0.7rem', sm: '0.8rem' },
                                    px: { xs: 1, sm: 2 },
                                    py: { xs: 0.5, sm: 1 },
                                }}
                            >
                                Fetch Range
                            </Button>
                        )}
                        
                        <Tooltip title="Refresh appointments">
                            <IconButton 
                                onClick={() => window.location.reload()} 
                                size="small"
                                sx={{
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                    '&:hover': { 
                                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                                        transform: 'scale(1.05)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                <Refresh sx={{ fontSize: { xs: 16, sm: 20 } }} />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>

                {/* Floating Parameter Chips - Compact */}
                <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} flexWrap="wrap" sx={{ mb: { xs: 2, sm: 2.5 } }}>
                    {/* Duration Chip */}
                    <Box>
                        {editingParam === 'duration' ? (
                            <FormControl size="small" sx={{ minWidth: { xs: 120, sm: 140 } }}>
                                <Select
                                    value={bookingForm.meeting_duration}
                                    onChange={(e) => {
                                        updateBookingForm('meeting_duration', Number(e.target.value));
                                        setEditingParam(null);
                                    }}
                                    autoFocus
                                    onBlur={() => setEditingParam(null)}
                                    sx={{ borderRadius: 10, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                                >
                                    <MenuItem value={15}>15 minutes</MenuItem>
                                    <MenuItem value={30}>30 minutes</MenuItem>
                                    <MenuItem value={45}>45 minutes</MenuItem>
                                    <MenuItem value={60}>1 hour</MenuItem>
                                    <MenuItem value={90}>1.5 hours</MenuItem>
                                    <MenuItem value={120}>2 hours</MenuItem>
                                </Select>
                            </FormControl>
                        ) : (
                            <Chip
                                icon={<Schedule sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                                label={`${bookingForm.meeting_duration} min`}
                                onClick={() => setEditingParam('duration')}
                                variant="outlined"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    borderRadius: 10,
                                    px: { xs: 0.5, sm: 1 },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                                                                            '&:hover': {
                                                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                                                            borderColor: theme.palette.primary.dark,
                                                        },
                                }}
                            />
                        )}
                    </Box>

                    {/* Location Chip */}
                    <Box>
                        {editingParam === 'location' ? (
                            <TextField
                                size="small"
                                placeholder="Enter location"
                                value={bookingForm.location}
                                onChange={(e) => updateBookingForm('location', e.target.value)}
                                onBlur={() => setEditingParam(null)}
                                onKeyPress={(e) => e.key === 'Enter' && setEditingParam(null)}
                                autoFocus
                                sx={{ 
                                    minWidth: { xs: 140, sm: 180 },
                                    '& .MuiOutlinedInput-root': { borderRadius: 10 },
                                    '& .MuiInputBase-input': { fontSize: { xs: '0.8rem', sm: '0.875rem' } }
                                }}
                            />
                        ) : (
                            <Chip
                                icon={<LocationOn sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                                label={bookingForm.location || 'Add location'}
                                onClick={() => setEditingParam('location')}
                                variant="outlined"
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                    borderRadius: 10,
                                    px: { xs: 0.5, sm: 1 },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                                    borderColor: alpha(theme.palette.secondary.main, 0.3),
                                    color: bookingForm.location ? 'text.primary' : 'text.secondary',
                                                                                                '&:hover': {
                                                                bgcolor: alpha(theme.palette.secondary.main, 0.15),
                                                                borderColor: theme.palette.secondary.dark,
                                                            },
                                }}
                            />
                        )}
                    </Box>

                    {/* Advanced Settings Toggle */}
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Chip
                            icon={<Tune sx={{ fontSize: { xs: 16, sm: 20 } }} />}
                            label="Advanced"
                            onClick={() => setShowAdvanced(!showAdvanced)}
                            variant={showAdvanced ? "filled" : "outlined"}
                            size={isMobile ? "small" : "medium"}
                            sx={{
                                borderRadius: 10,
                                px: { xs: 0.5, sm: 1 },
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                bgcolor: showAdvanced 
                                    ? theme.palette.info.main 
                                    : alpha(theme.palette.info.main, 0.05),
                                borderColor: alpha(theme.palette.info.main, 0.3),
                                color: showAdvanced ? 'white' : theme.palette.info.main,
                                                                                '&:hover': {
                                                    bgcolor: showAdvanced 
                                                        ? theme.palette.info.dark 
                                                        : alpha(theme.palette.info.main, 0.15),
                                                    borderColor: theme.palette.info.dark,
                                                },
                            }}
                        />
                    </Box>
                </Stack>

                {/* Advanced Settings Collapse */}
                <Collapse in={showAdvanced}>
                    <Card sx={{ mb: 3, borderRadius: 3, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
                        <CardContent sx={{ p: 2 }}>
                            <Stack direction="row" spacing={2} flexWrap="wrap">
                                {/* Summary/Title */}
                                <Box>
                                    {editingParam === 'summary' ? (
                                        <TextField
                                            size="small"
                                            placeholder="Meeting title"
                                            value={bookingForm.summary}
                                            onChange={(e) => updateBookingForm('summary', e.target.value)}
                                            onBlur={() => setEditingParam(null)}
                                            onKeyPress={(e) => e.key === 'Enter' && setEditingParam(null)}
                                            autoFocus
                                            sx={{ 
                                                minWidth: 200,
                                                '& .MuiOutlinedInput-root': { borderRadius: 8 }
                                            }}
                                        />
                                    ) : (
                                        <Chip
                                            icon={<Title />}
                                            label={bookingForm.summary || 'Add title'}
                                            onClick={() => setEditingParam('summary')}
                                            size="small"
                                            variant="outlined"
                                            sx={{ borderRadius: 8 }}
                                        />
                                    )}
                                </Box>

                                {/* Notes */}
                                <Box>
                                    {editingParam === 'notes' ? (
                                        <TextField
                                            size="small"
                                            placeholder="Add notes"
                                            value={bookingForm.client_notes}
                                            onChange={(e) => updateBookingForm('client_notes', e.target.value)}
                                            onBlur={() => setEditingParam(null)}
                                            onKeyPress={(e) => e.key === 'Enter' && setEditingParam(null)}
                                            autoFocus
                                            multiline
                                            rows={2}
                                            sx={{ 
                                                minWidth: 200,
                                                '& .MuiOutlinedInput-root': { borderRadius: 8 }
                                            }}
                                        />
                                    ) : (
                                        <Chip
                                            icon={<Notes />}
                                            label={bookingForm.client_notes || 'Add notes'}
                                            onClick={() => setEditingParam('notes')}
                                            size="small"
                                            variant="outlined"
                                            sx={{ borderRadius: 8 }}
                                        />
                                    )}
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Collapse>
            </Box>

            {/* Simplified Date Navigation with Horizontal Scroll */}
            <Box
                sx={{ mb: { xs: 2, sm: 3 } }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, mb: { xs: 2, sm: 2.5 } }}>
                    {/* Left Arrow */}
                    <Tooltip title="Previous week">
                        <IconButton 
                            onClick={() => handleDateNavigation('previous')}
                            disabled={isLoadingSlots || dateOffset === 0}
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': { 
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    transform: 'scale(1.1)',
                                },
                                '&:disabled': { 
                                    opacity: 0.3,
                                    transform: 'none',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <ChevronLeft sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        </IconButton>
                    </Tooltip>

                    {/* Scrollable Date Container */}
                    <Box 
                        sx={{ 
                            flex: 1, 
                            overflow: 'auto',
                            scrollbarWidth: 'thin',
                            '&::-webkit-scrollbar': {
                                height: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                borderRadius: '3px',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                bgcolor: alpha(theme.palette.primary.main, 0.2),
                                borderRadius: '3px',
                                '&:hover': {
                                    bgcolor: alpha(theme.palette.primary.main, 0.3),
                                },
                            },
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5 }, pb: 1 }}>
                            <AnimatePresence>
                                {allDates.map((date, index) => {
                                    const slotsCount = slotsByDate.get(date)?.length || 0;
                                    const isActive = activeDate === date;
                                    const dateObj = new Date(date + 'T00:00:00');
                                    const hasSlots = slotsCount > 0;
                                    const isFetched = isDateFetched(date);
                                    const dateLabel = formatDateLabel(date);
                                    const isLoadingThis = isLoadingSlots && isActive;
                                    const isInRange = dateRangeStart && dateRangeEnd && 
                                        date >= dateRangeStart && date <= dateRangeEnd;
                                    const isRangeStart = date === dateRangeStart;
                                    const isRangeEnd = date === dateRangeEnd;
                                
                                    return (
                                        <Box key={date} sx={{ flexShrink: 0 }}>
                                            <Card
                                                onClick={() => isSelectingRange ? handleDateRangeClick(date) : handleDateClick(date)}
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    width: { xs: 80, sm: 100 }, // Fixed width for square shape
                                                    height: { xs: 80, sm: 100 }, // Same as width for square
                                                        background: isActive || isInRange
                                                            ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`
                                                            : isFetched && hasSlots
                                                                ? `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.15)} 0%, ${alpha(theme.palette.success.light, 0.1)} 100%)`
                                                                : isFetched && !hasSlots
                                                                    ? `linear-gradient(135deg, ${alpha(theme.palette.grey[400], 0.2)} 0%, ${alpha(theme.palette.grey[300], 0.15)} 100%)`
                                                                    : `linear-gradient(135deg, ${alpha(theme.palette.grey[300], 0.3)} 0%, ${alpha(theme.palette.grey[200], 0.2)} 100%)`,
                                                        border: isActive || isRangeStart || isRangeEnd
                                                            ? `2px solid ${theme.palette.primary.main}`
                                                            : isInRange
                                                                ? `2px solid ${alpha(theme.palette.primary.main, 0.5)}`
                                                                : isFetched && hasSlots
                                                                    ? `2px solid ${alpha(theme.palette.success.main, 0.3)}`
                                                                    : !isFetched
                                                                        ? `2px dashed ${alpha(theme.palette.grey[400], 0.4)}`
                                                                        : `2px solid ${alpha(theme.palette.divider, 0.2)}`,
                                                        borderRadius: 3,
                                                        overflow: 'hidden',
                                                        opacity: !isFetched ? 0.6 : 1,
                                                        boxShadow: isActive || isInRange
                                                            ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                                                            : isFetched && hasSlots
                                                                ? `0 3px 12px ${alpha(theme.palette.success.main, 0.2)}`
                                                                : theme.shadows[1],
                                                        '&:hover': {
                                                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                                                            border: `2px solid ${theme.palette.primary.main}`,
                                                            opacity: 1,
                                                        },
                                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    }}
                                                                                                    >
                                                    {/* Loading overlay for this date */}
                                                    {isLoadingThis && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                zIndex: 3,
                                                                backdropFilter: 'blur(4px)',
                                                            }}
                                                        >
                                                            <CircularProgress size={16} />
                                                        </Box>
                                                    )}
                                                    
                                                    {/* Today indicator */}
                                                    {isToday(dateObj) && (
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 4,
                                                                right: 4,
                                                                bgcolor: theme.palette.warning.main,
                                                                color: 'white',
                                                                borderRadius: '50%',
                                                                width: 12,
                                                                height: 12,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontSize: '0.5rem',
                                                                fontWeight: 'bold',
                                                                boxShadow: theme.shadows[2],
                                                                zIndex: 2,
                                                            }}
                                                        >
                                                            ●
                                                        </Box>
                                                    )}
                                                    
                                                    <CardContent 
                                                        sx={{ 
                                                            p: { xs: 0.75, sm: 1 }, 
                                                            height: '100%',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            justifyContent: 'center',
                                                            alignItems: 'center',
                                                            textAlign: 'center',
                                                            '&:last-child': { pb: { xs: 0.75, sm: 1 } },
                                                        }}
                                                    >
                                                        <Typography 
                                                            variant="h5" 
                                                            sx={{ 
                                                                fontWeight: 800,
                                                                color: (isActive || isInRange) 
                                                                    ? 'white' 
                                                                    : isFetched && hasSlots
                                                                        ? theme.palette.success.dark
                                                                        : theme.palette.text.primary,
                                                                mb: 0.25,
                                                                fontSize: { xs: '1rem', sm: '1.2rem' },
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            {format(dateObj, 'd')}
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption"
                                                            sx={{ 
                                                                color: (isActive || isInRange) 
                                                                    ? alpha('#fff', 0.9) 
                                                                    : isFetched && hasSlots
                                                                        ? theme.palette.success.dark
                                                                        : theme.palette.text.secondary,
                                                                fontWeight: 600,
                                                                fontSize: { xs: '0.6rem', sm: '0.65rem' },
                                                                lineHeight: 1,
                                                            }}
                                                        >
                                                            {dateLabel}
                                                        </Typography>
                                                        
                                                        {/* Slots count or Unfetched indicator at bottom */}
                                                        {isFetched && hasSlots ? (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    mt: 0.5,
                                                                    color: (isActive || isInRange) ? 'white' : theme.palette.success.dark,
                                                                    fontWeight: 700,
                                                                    fontSize: '0.55rem',
                                                                }}
                                                            >
                                                                {slotsCount} slots
                                                            </Typography>
                                                        ) : !isFetched ? (
                                                            <Typography
                                                                variant="caption"
                                                                sx={{
                                                                    mt: 0.5,
                                                                    color: (isActive || isInRange) ? 'white' : theme.palette.text.primary,
                                                                    fontWeight: 600,
                                                                    fontSize: '0.55rem',
                                                                }}
                                                            >
                                                                Click to load
                                                            </Typography>
                                                        ) : null}
                                                    </CardContent>
                                                </Card>
                                            </Box>
                                    );
                                })}
                            </AnimatePresence>
                        </Box>
                    </Box>

                    {/* Right Arrow */}
                    <Tooltip title="Next week">
                        <IconButton 
                            onClick={() => handleDateNavigation('next')}
                            disabled={isLoadingSlots}
                            size="small"
                            sx={{
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                '&:hover': { 
                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                    transform: 'scale(1.1)',
                                },
                                transition: 'all 0.2s ease',
                            }}
                        >
                            <ChevronRight sx={{ fontSize: { xs: 18, sm: 20 } }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* Modern Time Slots Display */}
            <Box
                sx={{ position: 'relative' }}
            >
            {/* Local Loading Overlay */}
            {isLoadingSlots && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: alpha(theme.palette.background.paper, 0.8),
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 2,
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body2" color="text.secondary">
                        Loading appointments...
                    </Typography>
                </Box>
            )}
            {!Array.isArray(calendarData.available_slots) ? (
                    <Grow in timeout={800}>
                        <Card
                            sx={{
                                textAlign: 'center',
                                p: 4,
                                borderRadius: 4,
                                background: darkMode
                                    ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`
                                    : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)`,
                                backdropFilter: 'blur(20px)',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                boxShadow: `0 8px 32px ${alpha(theme.palette.warning.main, 0.15)}`,
                            }}
                        >
                            <Avatar
                                sx={{
                                    bgcolor: alpha(theme.palette.warning.main, 0.1),
                                    color: theme.palette.warning.main,
                                    width: 60,
                                    height: 60,
                                    mx: 'auto',
                                    mb: 2,
                                }}
                            >
                                <EventBusy sx={{ fontSize: 36 }} />
                            </Avatar>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        No Available Times
                    </Typography>
                            <Typography variant="body1" color="text.primary" sx={{ maxWidth: 400, mx: 'auto', opacity: 0.8 }}>
                                No {bookingForm.meeting_duration}-minute appointments found for the selected criteria
                    </Typography>
                        </Card>
                    </Grow>
            ) : activeSlots.length > 0 ? (
                <Box>
                        {/* Selected Date Header */}
                        <MotionCard
                            sx={{
                                mb: { xs: 2, sm: 3 },
                                borderRadius: { xs: 3, sm: 4 },
                                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                color: 'white',
                                overflow: 'hidden',
                            }}
                        >
                            <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'row', sm: 'column' }, alignItems: { xs: 'center', sm: 'flex-start' }, gap: { xs: 2, sm: 0.5 }, flex: 1 }}>
                                        <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1rem', sm: '1.5rem' }, lineHeight: 1 }}>
                                            {formatDate(activeDate, true)}
                                        </Typography>
                                        <Typography variant="body1" sx={{ opacity: 0.9, fontSize: { xs: '0.75rem', sm: '1rem' }, lineHeight: 1 }}>
                                            {activeSlots.length} slot{activeSlots.length !== 1 ? 's' : ''}
                                        </Typography>
                                    </Box>
                                    <Avatar
                                        sx={{
                                            bgcolor: alpha('#fff', 0.2),
                                            width: { xs: 32, sm: 48 },
                                            height: { xs: 32, sm: 48 },
                                        }}
                                    >
                                        <CalendarToday sx={{ fontSize: { xs: 16, sm: 24 }, color: 'white' }} />
                                    </Avatar>
                                </Box>
                            </CardContent>
                        </MotionCard>

                        {/* Creative Time Slots by Period */}
                        <Stack spacing={{ xs: 1.5, sm: 2.5 }}>
                            {timePeriodsSlots.map(([periodKey, period], periodIndex) => (
                                <Box key={periodKey}>
                                    <Card
                                        sx={{
                                            borderRadius: { xs: 3, sm: 4 },
                                            overflow: 'hidden',
                                            border: `1px solid ${alpha(period.color, 0.2)}`,
                                            boxShadow: `0 4px 20px ${alpha(period.color, 0.1)}`,
                                        }}
                                    >
                                        {/* Period header */}
                                        <Box
                                            sx={{
                                                background: `linear-gradient(90deg, ${alpha(period.color, 0.1)} 0%, ${alpha(period.color, 0.05)} 100%)`,
                                                p: { xs: 1, sm: 1.5 },
                                                borderBottom: `1px solid ${alpha(period.color, 0.1)}`,
                                            }}
                                        >
                                            <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                                <Box
                                                    sx={{
                                                        fontSize: { xs: '0.8rem', sm: '1rem' },
                                                        background: alpha(period.color, 0.1),
                                                        borderRadius: '50%',
                                                        width: { xs: 24, sm: 28 },
                                                        height: { xs: 24, sm: 28 },
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    {period.icon}
                                                </Box>
                                                {period.label}
                                                <Chip 
                                                    label={`${period.slots.length} slot${period.slots.length !== 1 ? 's' : ''}`} 
                                                    size="small"
                                                    sx={{
                                                        bgcolor: period.color,
                                                        color: 'white',
                                                        fontWeight: 600,
                                                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                                                        height: { xs: 18, sm: 20 },
                                                    }}
                                                />
                                            </Typography>
                                        </Box>

                                        {/* Compact pill-style time slots */}
                                        <CardContent sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: { xs: 1, sm: 1.5 },
                                                    justifyContent: 'flex-start',
                                                }}
                                            >
                                                {period.slots.map((slot, slotIndex) => (
                                                    <Box
                                                        key={slotIndex}
                                                    >
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => handleSlotSelect(slot)}
                                                    size={isMobile ? "small" : "medium"}
                                                    sx={{ 
                                                                    minWidth: 'auto',
                                                                    px: { xs: 2, sm: 2.5 },
                                                        py: { xs: 0.6, sm: 0.8 },
                                                                    borderRadius: 20,
                                                                    textTransform: 'none',
                                                                    fontWeight: 700,
                                                                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                                                                    background: `linear-gradient(135deg, ${alpha(period.color, 0.05)} 0%, ${alpha(period.color, 0.02)} 100%)`,
                                                                    borderColor: alpha(period.color, 0.3),
                                                                    color: period.color,
                                                                    whiteSpace: 'nowrap',
                                                        '&:hover': {
                                                                        background: `linear-gradient(135deg, ${period.color} 0%, ${alpha(period.color, 0.8)} 100%)`,
                                                                        borderColor: period.color,
                                                                        color: 'white',
                                                                        boxShadow: `0 6px 20px ${alpha(period.color, 0.4)}`,
                                                                        transform: 'translateY(-2px)',
                                                                    },
                                                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                    }}
                                                >
                                                    {formatTime(slot.start_time)}
                                                </Button>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Box>
                            ))}
                        </Stack>
                    </Box>
                ) : (
                    <Grow in timeout={800}>
                        <Card
                            sx={{
                                borderRadius: 4,
                                background: darkMode
                                    ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`
                                    : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)`,
                                backdropFilter: 'blur(20px)',
                                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                boxShadow: `0 8px 32px ${alpha(theme.palette.info.main, 0.15)}`,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                                    color: 'white',
                                    p: { xs: 2, sm: 3 },
                                    textAlign: 'center',
                                }}
                            >
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: { xs: 0.5, sm: 1 }, fontSize: { xs: '0.9rem', sm: '1.25rem' } }}>
                                    No Times Available on {formatDate(activeDate, true)}
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontSize: { xs: '0.7rem', sm: '0.875rem' } }}>
                                    But we found {calendarData.available_slots.length} available appointments on other dates
                                </Typography>
                            </Box>
                            
                            {/* Scrollable list of all available appointments */}
                            <Box
                                sx={{
                                    maxHeight: 500,
                                    overflowY: 'auto',
                                    p: 3,
                                    '&::-webkit-scrollbar': {
                                        width: '8px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        borderRadius: '4px',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                                        borderRadius: '4px',
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, 0.3),
                                        },
                                    },
                                }}
                            >
                                <Stack spacing={3}>
                                    {allSlotsGrouped.map((dayGroup, index) => (
                                        <Box
                                            key={dayGroup.date}
                                        >
                                            <Card
                                                sx={{
                                                    borderRadius: 3,
                                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                                                    },
                                                }}
                                                onClick={() => {
                                                    setSelectedDate(dayGroup.date);
                                                    handleDateClick(dayGroup.date);
                                                }}
                                            >
                                                {/* Date Header */}
                                                <Box
                                                    sx={{
                                                        background: `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                                                        p: 2,
                                                        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                            <CalendarToday sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                                                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                                {formatDate(dayGroup.date, true)}
                                                            </Typography>
                                                        </Box>
                                                        <Chip
                                                            label={`${dayGroup.totalSlots} slots`}
                                                            size="small"
                                                            color="primary"
                                                            sx={{ fontWeight: 600 }}
                                                        />
                                                    </Stack>
                                                </Box>

                                                {/* Time Slots by Period */}
                                                <CardContent sx={{ p: 2 }}>
                                                    <Stack spacing={2}>
                                                        {/* Morning */}
                                                        {dayGroup.periods.morning.length > 0 && (
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                                                                    🌅 Morning
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                    {dayGroup.periods.morning.map((slot, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={formatTime(slot.start_time)}
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSlotSelect(slot);
                                                                            }}
                                                                            sx={{
                                                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                                                color: theme.palette.primary.main,
                                                                                fontWeight: 600,
                                                                                '&:hover': {
                                                                                    bgcolor: theme.palette.primary.main,
                                                                                    color: 'white',
                                                                                },
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {/* Afternoon */}
                                                        {dayGroup.periods.afternoon.length > 0 && (
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                                                                    ☀️ Afternoon
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                    {dayGroup.periods.afternoon.map((slot, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={formatTime(slot.start_time)}
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSlotSelect(slot);
                                                                            }}
                                                                            sx={{
                                                                                bgcolor: alpha(theme.palette.warning.main, 0.1),
                                                                                color: theme.palette.warning.main,
                                                                                fontWeight: 600,
                                                                                '&:hover': {
                                                                                    bgcolor: theme.palette.warning.main,
                                                                                    color: 'white',
                                                                                },
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}

                                                        {/* Evening */}
                                                        {dayGroup.periods.evening.length > 0 && (
                                                            <Box>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1, display: 'block' }}>
                                                                    🌆 Evening
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                    {dayGroup.periods.evening.map((slot, idx) => (
                                                                        <Chip
                                                                            key={idx}
                                                                            label={formatTime(slot.start_time)}
                                                                            size="small"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleSlotSelect(slot);
                                                                            }}
                                                                            sx={{
                                                                                bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                                                color: theme.palette.secondary.main,
                                                                                fontWeight: 600,
                                                                                '&:hover': {
                                                                                    bgcolor: theme.palette.secondary.main,
                                                                                    color: 'white',
                                                                                },
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            </Box>
                                                        )}
                                                    </Stack>
                                                </CardContent>
                                            </Card>
                                        </Box>
                                    ))}
                                </Stack>
                            </Box>
                        </Card>
                    </Grow>
                )}
            </Box>
        </>
    );

    const renderBookingView = () => (
        <Box 
            sx={{
                minHeight: 'auto',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                justifyContent: 'center',
                p: 0,
            }}
        >
            <Card
                sx={{
                    maxWidth: 600,
                    width: '100%',
                    borderRadius: 4,
                    background: darkMode
                        ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`
                        : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)`,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    boxShadow: `0 20px 60px ${alpha(theme.palette.primary.main, 0.15)}`,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                        p: 3,
                        borderRadius: '16px 16px 0 0',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -50,
                            left: -50,
                            width: 150,
                            height: 150,
                            bgcolor: alpha('#fff', 0.1),
                            borderRadius: '50%',
                        }}
                    />
                    <Box sx={{ position: 'relative' }}>
                        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                            <IconButton 
                                onClick={() => setViewState('calendar')} 
                                sx={{ color: 'white' }}
                            >
                                <ArrowBack />
                            </IconButton>
                            <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
                                <Event sx={{ color: 'white' }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h5" fontWeight={700}>
                                    Confirm Your Appointment
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                    Review and customize your booking details
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Box>

                <CardContent sx={{ p: 4 }}>
                    {selectedSlot && (
                        <Stack spacing={4}>
                            {/* Date & Time Display */}
                            <Box>
                                <Card sx={{ 
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderRadius: 3
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                                            📅 {formatDate(selectedSlot.start_time.split('T')[0], true)}
                                        </Typography>
                                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                                            ⏰ {formatTime(selectedSlot.start_time, true)} - {formatTime(selectedSlot.end_time, true)}
                                        </Typography>
                                        <Typography color="text.secondary">
                                            Duration: {bookingForm.meeting_duration} minutes
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Box>

                            {/* WYSIWYG Booking Form */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Edit sx={{ color: theme.palette.secondary.main }} />
                                    Booking Details
                                </Typography>
                                
                                <Stack spacing={3}>
                                    {/* Meeting Title */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Meeting Title *
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={bookingForm.summary}
                                            onChange={(e) => updateBookingForm('summary', e.target.value)}
                                            placeholder="Enter meeting title"
                                            variant="outlined"
                                            InputProps={{
                                                startAdornment: <Title sx={{ mr: 1, color: 'text.secondary' }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    '&:hover': {
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.primary.main,
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Location */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Location
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            value={bookingForm.location}
                                            onChange={(e) => updateBookingForm('location', e.target.value)}
                                            placeholder="Meeting location (optional)"
                                            variant="outlined"
                                            InputProps={{
                                                startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    '&:hover': {
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.secondary.main,
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Duration Adjustment */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Duration
                                        </Typography>
                                        <FormControl fullWidth>
                                            <Select
                                                value={bookingForm.meeting_duration}
                                                onChange={(e) => updateBookingForm('meeting_duration', Number(e.target.value))}
                                                startAdornment={<Schedule sx={{ mr: 1, color: 'text.secondary' }} />}
                                                sx={{
                                                    borderRadius: 3,
                                                    '&:hover': {
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.info.main,
                                                        },
                                                    },
                                                }}
                                            >
                                                <MenuItem value={15}>15 minutes</MenuItem>
                                                <MenuItem value={30}>30 minutes</MenuItem>
                                                <MenuItem value={45}>45 minutes</MenuItem>
                                                <MenuItem value={60}>1 hour</MenuItem>
                                                <MenuItem value={90}>1.5 hours</MenuItem>
                                                <MenuItem value={120}>2 hours</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>

                                    {/* Client Notes */}
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                            Additional Notes
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={bookingForm.client_notes}
                                            onChange={(e) => updateBookingForm('client_notes', e.target.value)}
                                            placeholder="Any special requests or notes for this meeting?"
                                            variant="outlined"
                                            InputProps={{
                                                startAdornment: (
                                                    <Notes sx={{ 
                                                        mr: 1, 
                                                        color: 'text.secondary',
                                                        alignSelf: 'flex-start',
                                                        mt: 1
                                                    }} />
                                                )
                                            }}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 3,
                                                    '&:hover': {
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: theme.palette.warning.main,
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>

                                    {/* Advanced Options */}
                                    <Accordion 
                                        expanded={showAdvanced} 
                                        onChange={() => setShowAdvanced(!showAdvanced)}
                                        sx={{ 
                                            boxShadow: 'none',
                                            border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                            borderRadius: 3,
                                            '&:before': { display: 'none' },
                                        }}
                                    >
                                        <AccordionSummary 
                                            expandIcon={<ExpandMore />}
                                            sx={{ 
                                                '& .MuiAccordionSummary-content': { 
                                                    alignItems: 'center' 
                                                }
                                            }}
                                        >
                                            <Tune sx={{ mr: 1, color: 'text.secondary' }} />
                                            <Typography variant="subtitle2" fontWeight={600}>
                                                Advanced Options
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Stack spacing={3}>
                                                {/* Attendees */}
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                        Attendees
                                                    </Typography>
                                                    <Stack spacing={1}>
                                                        {bookingForm.attendees.map((attendee, index) => (
                                                            <Stack key={index} direction="row" spacing={1} alignItems="center">
                                                                <TextField
                                                                    fullWidth
                                                                    size="small"
                                                                    value={attendee}
                                                                    onChange={(e) => {
                                                                        const newAttendees = [...bookingForm.attendees];
                                                                        newAttendees[index] = e.target.value;
                                                                        updateBookingForm('attendees', newAttendees);
                                                                    }}
                                                                    placeholder="Email address"
                                                                    type="email"
                                                                    InputProps={{
                                                                        startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                                                                    }}
                                                                    sx={{
                                                                        '& .MuiOutlinedInput-root': {
                                                                            borderRadius: 2,
                                                                        },
                                                                    }}
                                                                />
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        const newAttendees = bookingForm.attendees.filter((_, i) => i !== index);
                                                                        updateBookingForm('attendees', newAttendees);
                                                                    }}
                                                                    sx={{ color: 'error.main' }}
                                                                >
                                                                    <Delete />
                                                                </IconButton>
                                                            </Stack>
                                                        ))}
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            startIcon={<Person />}
                                                            onClick={() => updateBookingForm('attendees', [...bookingForm.attendees, ''])}
                                                            sx={{
                                                                borderRadius: 2,
                                                                borderStyle: 'dashed',
                                                                color: 'text.secondary',
                                                                borderColor: alpha(theme.palette.divider, 0.5),
                                                                '&:hover': {
                                                                    borderColor: theme.palette.primary.main,
                                                                    color: theme.palette.primary.main,
                                                                },
                                                            }}
                                                        >
                                                            Add Attendee
                                                        </Button>
                                                    </Stack>
                                                </Box>

                                                {/* Description */}
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                                        Description
                                                    </Typography>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={2}
                                                        value={bookingForm.description}
                                                        onChange={(e) => updateBookingForm('description', e.target.value)}
                                                        placeholder="Additional details about the meeting"
                                                        variant="outlined"
                                                        sx={{
                                                            '& .MuiOutlinedInput-root': {
                                                                borderRadius: 2,
                                                            },
                                                        }}
                                                    />
                                                </Box>

                                                {/* Send Notifications */}
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={bookingForm.send_notifications}
                                                            onChange={(e) => updateBookingForm('send_notifications', e.target.checked)}
                                                            color="primary"
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                Send Email Notifications
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Send calendar invites to all attendees
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />

                                                {/* Double Booking */}
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={bookingForm.allow_double_booking}
                                                            onChange={(e) => updateBookingForm('allow_double_booking', e.target.checked)}
                                                            color="warning"
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600}>
                                                                Allow Double Booking
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Force this booking even if it conflicts with existing appointments
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Stack>
                                        </AccordionDetails>
                                    </Accordion>
                                </Stack>
                            </Box>

                            {/* Error Display with Force Booking */}
                            {bookingError && (
                                <Box>
                                    <Alert 
                                        severity={bookingForm.allow_double_booking ? "warning" : "error"}
                                        sx={{ borderRadius: 3, mb: 2 }}
                                    >
                                        <Typography variant="body2" fontWeight={600}>
                                            {bookingError}
                                        </Typography>
                                        {!bookingForm.allow_double_booking && bookingError.toLowerCase().includes('conflict') && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    This time slot conflicts with an existing appointment.
                                                </Typography>
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            checked={bookingForm.allow_double_booking}
                                                            onChange={(e) => updateBookingForm('allow_double_booking', e.target.checked)}
                                                            color="warning"
                                                        />
                                                    }
                                                    label={
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600} color="warning.main">
                                                                Force this booking anyway
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Override the conflict and book this appointment
                                                            </Typography>
                                                        </Box>
                                                    }
                                                />
                                            </Box>
                                        )}
                                        {bookingForm.allow_double_booking && (
                                            <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                                                Click "Force Book Anyway" below to proceed with the booking despite the conflict.
                                            </Typography>
                                        )}
                                    </Alert>
                                </Box>
                            )}

                            {/* Action Buttons */}
                            <Box>
                                <Stack direction="row" spacing={2}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => setViewState('calendar')}
                                        disabled={isBooking}
                                        fullWidth
                                        size="large"
                                        sx={{ 
                                            borderRadius: 3,
                                            py: 1.5,
                                            borderColor: alpha(theme.palette.primary.main, 0.3),
                                            '&:hover': {
                                                borderColor: theme.palette.primary.main,
                                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                                            },
                                        }}
                                    >
                                        Back to Calendar
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={handleBooking}
                                        disabled={isBooking || !bookingForm.summary.trim()}
                                        fullWidth
                                        size="large"
                                        sx={{ 
                                            borderRadius: 3,
                                            py: 1.5,
                                            fontWeight: 700,
                                            background: bookingForm.allow_double_booking
                                                ? `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`
                                                : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                            '&:hover': {
                                                boxShadow: `0 8px 24px ${alpha(
                                                    bookingForm.allow_double_booking ? theme.palette.warning.main : theme.palette.primary.main, 
                                                    0.4
                                                )}`,
                                                transform: 'translateY(-2px)',
                                            },
                                            '&:disabled': {
                                                background: alpha(theme.palette.primary.main, 0.3),
                                                transform: 'none',
                                            },
                                            transition: 'all 0.3s ease',
                                        }}
                                    >
                                        {isBooking ? (
                                            <Stack direction="row" alignItems="center" spacing={1}>
                                                <CircularProgress size={20} color="inherit" />
                                                <Typography>Booking...</Typography>
                                            </Stack>
                                        ) : bookingForm.allow_double_booking ? (
                                            'Force Book Anyway'
                                        ) : (
                                            'Confirm Booking'
                                        )}
                                    </Button>
                                </Stack>
                            </Box>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Box>
    );

    const renderConfirmedView = () => (
        <Box 
            sx={{
                minHeight: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                p: 0,
            }}
        >
            <Card
                sx={{
                    maxWidth: 700,
                    width: '100%',
                    borderRadius: 4,
                    background: darkMode
                        ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`
                        : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)`,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                    boxShadow: `0 20px 60px ${alpha(theme.palette.success.main, 0.15)}`,
                    overflow: 'hidden',
                }}
            >
                {/* Success Header */}
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                        color: 'white',
                        p: 4,
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {/* Decorative elements */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -100,
                            left: -100,
                            width: 200,
                            height: 200,
                            bgcolor: alpha('#fff', 0.1),
                            borderRadius: '50%',
                        }}
                    />
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: -75,
                            right: -75,
                            width: 150,
                            height: 150,
                            bgcolor: alpha('#fff', 0.05),
                            borderRadius: '50%',
                        }}
                    />
                    
                    <Box sx={{ position: 'relative', zIndex: 1 }}>
                        <Box>
                            <CheckCircle sx={{ fontSize: 80, mb: 2 }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" fontWeight={800} gutterBottom>
                                Booking Confirmed!
                            </Typography>
                            <Typography variant="h6" sx={{ opacity: 0.9 }}>
                                Your appointment has been successfully scheduled
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                <CardContent sx={{ p: 4 }}>
                    {createdEvent && (
                        <Stack spacing={4}>
                            {/* Event Details Display */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Event sx={{ color: theme.palette.primary.main }} />
                                    Appointment Details
                                </Typography>
                                
                                <Card sx={{ 
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderRadius: 3
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                    <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                                                        <CalendarToday />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Date & Time
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={600}>
                                                            {createdEvent.start?.date_time && format(new Date(createdEvent.start.date_time), 'EEEE, MMMM d, yyyy')}
                                                        </Typography>
                                                        <Typography variant="body1" color="primary" fontWeight={600}>
                                                            {createdEvent.start?.date_time && format(new Date(createdEvent.start.date_time), 'h:mm a')}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            
                                            <Grid item xs={12} md={6}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                    <Avatar sx={{ bgcolor: theme.palette.secondary.main }}>
                                                        <Schedule />
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Duration
                                                        </Typography>
                                                        <Typography variant="h6" fontWeight={600}>
                                                            {bookingForm.meeting_duration} minutes
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                            
                                            {createdEvent.summary && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                        <Avatar sx={{ bgcolor: theme.palette.info.main }}>
                                                            <Title />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                Meeting Title
                                                            </Typography>
                                                            <Typography variant="h6" fontWeight={600}>
                                                                {createdEvent.summary}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            )}
                                            
                                            {createdEvent.location && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                                        <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                                                            <LocationOn />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                Location
                                                            </Typography>
                                                            <Typography variant="body1" fontWeight={600}>
                                                                {createdEvent.location}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            )}
                                            
                                            {bookingForm.client_notes && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                                        <Avatar sx={{ bgcolor: theme.palette.grey[600] }}>
                                                            <Notes />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                Notes
                                                            </Typography>
                                                            <Typography variant="body1">
                                                                {bookingForm.client_notes}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            )}

                                            {createdEvent.attendees && createdEvent.attendees.length > 0 && (
                                                <Grid item xs={12}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                                                        <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                                                            <Person />
                                                        </Avatar>
                                                        <Box>
                                                            <Typography variant="subtitle2" color="text.secondary">
                                                                Attendees
                                                            </Typography>
                                                            {createdEvent.attendees.map((attendee, idx) => (
                                                                <Typography key={idx} variant="body1">
                                                                    {attendee.email || attendee.display_name}
                                                                    {attendee.organizer && ' (Organizer)'}
                                                                </Typography>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>

                            {/* Event Management Actions */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tune sx={{ color: theme.palette.primary.main }} />
                                    Event Management
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            size="large"
                                            startIcon={<Update />}
                                            color="info"
                                            onClick={handleUpdateEvent}
                                            sx={{
                                                borderRadius: 3,
                                                py: 1.5,
                                            }}
                                        >
                                            Update Event
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            size="large"
                                            startIcon={<Schedule />}
                                            color="warning"
                                            onClick={handleRescheduleEvent}
                                            sx={{
                                                borderRadius: 3,
                                                py: 1.5,
                                            }}
                                        >
                                            Reschedule
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            size="large"
                                            startIcon={<Delete />}
                                            color="error"
                                            onClick={handleCancelEvent}
                                            sx={{
                                                borderRadius: 3,
                                                py: 1.5,
                                            }}
                                        >
                                            Cancel Event
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Success Tips */}
                            <Box>
                                <Card sx={{ 
                                    bgcolor: alpha(theme.palette.success.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                                    borderRadius: 3
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <AutoAwesome sx={{ color: theme.palette.success.main }} />
                                            What's Next?
                                        </Typography>
                                        <List dense>
                                            <ListItem>
                                                <ListItemText 
                                                    primary="📧 Check your email for confirmation details"
                                                    secondary="You should receive a confirmation email shortly"
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText 
                                                    primary="📅 Add to your calendar"
                                                    secondary="Use the calendar link provided in your confirmation email"
                                                />
                                            </ListItem>
                                            <ListItem>
                                                <ListItemText 
                                                    primary="✏️ Make changes if needed"
                                                    secondary="You can modify or cancel this appointment using the options above"
                                                />
                                            </ListItem>
                                        </List>
                                    </CardContent>
                                </Card>
                            </Box>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Box>
    );

    // Event details view with editing capabilities
    const renderEventDetailsView = () => (
        <Box 
            sx={{
                minHeight: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 2
            }}
        >
            <Card
                sx={{
                    maxWidth: 700,
                    width: '100%',
                    borderRadius: 4,
                    background: darkMode
                        ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.85)} 100%)`
                        : `linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.85) 100%)`,
                    backdropFilter: 'blur(20px)',
                    border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                    boxShadow: `0 20px 60px ${alpha(theme.palette.info.main, 0.15)}`,
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        background: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
                        color: 'white',
                        p: 3,
                        borderRadius: '16px 16px 0 0',
                    }}
                >
                    <Stack direction="row" alignItems="center" spacing={2}>
                        <IconButton 
                            onClick={() => setViewState('confirmed')} 
                            sx={{ color: 'white' }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <Avatar sx={{ bgcolor: alpha('#fff', 0.2) }}>
                            <Edit sx={{ color: 'white' }} />
                        </Avatar>
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Event Details & Management
                            </Typography>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                View, edit, or cancel your appointment
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                <CardContent sx={{ p: 4 }}>
                    {createdEvent && (
                        <Stack spacing={4}>
                            {/* Event Information */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Visibility sx={{ color: theme.palette.primary.main }} />
                                    Current Event Details
                                </Typography>
                                
                                <Card sx={{ 
                                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    borderRadius: 3
                                }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" color="text.secondary">Event ID</Typography>
                                                <Typography variant="body1" fontWeight={600}>{createdEvent.id}</Typography>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="body2" color="text.secondary">Status</Typography>
                                                <Chip label="Confirmed" color="success" size="small" />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Typography variant="body2" color="text.secondary">Summary</Typography>
                                                <Typography variant="h6" fontWeight={600}>{createdEvent.summary}</Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">Start Time</Typography>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {createdEvent.start?.date_time && format(new Date(createdEvent.start.date_time), 'PPpp')}
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <Typography variant="body2" color="text.secondary">Duration</Typography>
                                                <Typography variant="body1" fontWeight={600}>{bookingForm.meeting_duration} minutes</Typography>
                                            </Grid>
                                            {createdEvent.location && (
                                                <Grid item xs={12}>
                                                    <Typography variant="body2" color="text.secondary">Location</Typography>
                                                    <Typography variant="body1" fontWeight={600}>{createdEvent.location}</Typography>
                                                </Grid>
                                            )}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Box>

                            {/* Management Actions */}
                            <Box>
                                <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Tune sx={{ color: theme.palette.secondary.main }} />
                                    Management Actions
                                </Typography>
                                
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            size="large"
                                            startIcon={<Update />}
                                            color="info"
                                            sx={{
                                                borderRadius: 3,
                                                py: 1.5,
                                            }}
                                        >
                                            Update Event
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            size="large"
                                            startIcon={<Schedule />}
                                            color="warning"
                                            sx={{
                                                borderRadius: 3,
                                                py: 1.5,
                                            }}
                                        >
                                            Reschedule
                                        </Button>
                                    </Grid>
                                    
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            variant="outlined"
                                            fullWidth
                                            size="large"
                                            startIcon={<Delete />}
                                            color="error"
                                            sx={{
                                                borderRadius: 3,
                                                py: 1.5,
                                            }}
                                        >
                                            Cancel Event
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Back Button */}
                            <Button
                                variant="contained"
                                onClick={() => setViewState('confirmed')}
                                startIcon={<ArrowBack />}
                                sx={{
                                    borderRadius: 3,
                                    py: 1.5,
                                    alignSelf: 'flex-start',
                                }}
                            >
                                Back to Confirmation
                            </Button>
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Box>
    );

    return (
        <Box
            sx={{ 
                width: '100%',
                p: { xs: 0.5, sm: 3, md: 4 },
                minHeight: 'auto',
                bgcolor: darkMode ? 'transparent' : 'background.default',
            }}
        >
            {/* Render current view */}
            {viewState === 'calendar' && renderCalendarView()}
            {viewState === 'booking' && renderBookingView()}
            {viewState === 'confirmed' && renderConfirmedView()}
            {viewState === 'event_details' && renderEventDetailsView()}
            
            {/* Update Event Dialog */}
            <Dialog
                open={updateDialogOpen}
                onClose={() => setUpdateDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Update Event</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 2 }}>
                        {/* Summary */}
                        <TextField
                            fullWidth
                            label="Meeting Title"
                            value={updateForm.summary}
                            onChange={(e) => setUpdateForm(prev => ({ ...prev, summary: e.target.value }))}
                            InputProps={{
                                startAdornment: <Title sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        
                        {/* Location */}
                        <TextField
                            fullWidth
                            label="Location"
                            value={updateForm.location}
                            onChange={(e) => setUpdateForm(prev => ({ ...prev, location: e.target.value }))}
                            InputProps={{
                                startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                        
                        {/* Description */}
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            label="Description"
                            value={updateForm.description}
                            onChange={(e) => setUpdateForm(prev => ({ ...prev, description: e.target.value }))}
                        />
                        
                        {/* Attendees */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                Attendees
                            </Typography>
                            <Stack spacing={1}>
                                {updateForm.attendees.map((attendee, index) => (
                                    <Stack key={index} direction="row" spacing={1} alignItems="center">
                                        <TextField
                                            fullWidth
                                            size="small"
                                            value={attendee}
                                            onChange={(e) => {
                                                const newAttendees = [...updateForm.attendees];
                                                newAttendees[index] = e.target.value;
                                                setUpdateForm(prev => ({ ...prev, attendees: newAttendees }));
                                            }}
                                            placeholder="Email address"
                                            type="email"
                                            InputProps={{
                                                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                                            }}
                                        />
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                const newAttendees = updateForm.attendees.filter((_, i) => i !== index);
                                                setUpdateForm(prev => ({ ...prev, attendees: newAttendees }));
                                            }}
                                            sx={{ color: 'error.main' }}
                                        >
                                            <Delete />
                                        </IconButton>
                                    </Stack>
                                ))}
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<Person />}
                                    onClick={() => setUpdateForm(prev => ({ ...prev, attendees: [...prev.attendees, ''] }))}
                                    sx={{
                                        borderRadius: 2,
                                        borderStyle: 'dashed',
                                        color: 'text.secondary',
                                        borderColor: alpha(theme.palette.divider, 0.5),
                                        '&:hover': {
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                >
                                    Add Attendee
                                </Button>
                            </Stack>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpdateDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleUpdateSubmit} variant="contained">
                        Update Event
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default React.memo(CalendarView, (prevProps, nextProps) => {
  // Deep comparison for calendar data
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.channelId === nextProps.channelId &&
    prevProps.messageId === nextProps.messageId &&
    JSON.stringify(prevProps.calendarData) === JSON.stringify(nextProps.calendarData)
  );
}); 
